import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { ArrowLeft, Mail, Users, Copy, Check, User, Info, Send, Link, Sparkles, Heart, Baby, UserCheck, Smile, UsersRound, UserCircle2, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import welcomeBg from "../../assets/homebg.png";
import { useSendInvite } from "../../hooks/useFamily";
import type { SendInviteRequest, RelationshipType, InviteMethod } from "../../types/family";

const MEMBER_NAME_MAX = 50;
const EMAIL_MAX = 254; // RFC 5321 max email length
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteMemberPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupIdFromUrl = searchParams.get("group_id") || localStorage.getItem("family_group_id") || undefined;
  const [selectedRelation, setSelectedRelation] = useState<string>("");
  const [memberName, setMemberName] = useState<string>("");
  const [inviteMethod, setInviteMethod] = useState<InviteMethod>("link"); // Default to link method
  const [contactValue, setContactValue] = useState<string>("");
  const [inviteLinkCopied, setInviteLinkCopied] = useState<boolean>(false);
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const emailFieldRef = useRef<HTMLTextAreaElement>(null);

  // Single-line height until wrapping is needed, then grow with content (capped like max-h-40).
  useLayoutEffect(() => {
    if (inviteMethod !== "email") return;
    const el = emailFieldRef.current;
    if (!el) return;
    const capPx = 160; // ~max-h-40 (10rem at 16px root)
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, capPx)}px`;
  }, [contactValue, inviteMethod]);

  // Get family members from localStorage
  const [familyMembers, setFamilyMembers] = useState<Array<{user_id: string; display_name: string; initial: string; is_current_user: boolean}>>([]);

  useEffect(() => {
    const stored = localStorage.getItem("glucera_family_members");
    if (stored) {
      try {
        setFamilyMembers(JSON.parse(stored));
      } catch (error) {
        console.error("Error parsing family members:", error);
      }
    }
  }, []);

  // Query hooks
  const sendInviteMutation = useSendInvite();

  // Live email validity (used for inline guidance and to gate the submit button)
  const isEmailValid = EMAIL_REGEX.test(contactValue.trim());

  const relations = [
    { id: "spouse",  label: "Spouse",  icon: Heart,       color: "blue"    },
    { id: "parent",  label: "Parent",  icon: UserCheck,   color: "purple"  },
    { id: "child",   label: "Child",   icon: Baby,        color: "pink"    },
    { id: "sibling", label: "Sibling", icon: UsersRound,  color: "yellow"  },
    { id: "friend",  label: "Friend",  icon: Smile,       color: "cyan"    },
    { id: "other",   label: "Other",   icon: UserCircle2, color: "emerald" },
  ];

  const handleCopyLink = () => {
    const linkToCopy = generatedLink || "https://glucera.universal-software.in";
    navigator.clipboard.writeText(linkToCopy);
    setInviteLinkCopied(true);
    message.success("Invite link copied!");
    setTimeout(() => setInviteLinkCopied(false), 2000);
  };

  const handleSendInvite = async () => {
    // Validation
    if (!memberName.trim()) {
      message.error("Please enter member name");
      return;
    }
    if (!selectedRelation) {
      message.error("Please select relationship");
      return;
    }
    if (inviteMethod === "email" && !contactValue.trim()) {
      message.error("Please enter email address");
      return;
    }
    if (inviteMethod === "email") {
      // Basic email validation
      if (!EMAIL_REGEX.test(contactValue.trim())) {
        message.error("Please enter a valid email address");
        return;
      }
      
      // Check if user is trying to invite themselves
      const currentUserInfo = localStorage.getItem("glucera_user_info");
      if (currentUserInfo) {
        try {
          const userInfo = JSON.parse(currentUserInfo);
          const currentEmail = userInfo.email?.toLowerCase();
          const inviteEmail = contactValue.trim().toLowerCase();
          
          if (currentEmail && currentEmail === inviteEmail) {
            message.warning({
              content: "You cannot invite yourself! Please enter a different email address.",
              duration: 5,
            });
            return;
          }
        } catch (error) {
          console.error("Error parsing user info:", error);
        }
      }
    }

    // Validate UUID before including group_id
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const validGroupId = groupIdFromUrl && uuidRegex.test(groupIdFromUrl) ? groupIdFromUrl : undefined;

    // Prepare request
    const request: SendInviteRequest = {
      ...(inviteMethod === "email" && { email: contactValue }),
      member_name: memberName,
      relationship: selectedRelation as RelationshipType,
      invite_method: inviteMethod,
      ...(validGroupId && { group_id: validGroupId }),
    };

    try {
      const response = await sendInviteMutation.mutateAsync(request);
      
      if (response.invite_link) {
        setGeneratedLink(response.invite_link);
        
        if (inviteMethod === "link") {
          // Hook already shows generic success; show link-specific message only for link method
          message.success("Invite link generated! You can now copy and share it.");
        } else {
          // Email method — hook's onSuccess already fired; just redirect
          setTimeout(() => navigate(`/glucera${GluceraNavigationRoutes.HOMEPAGE}`), 1500);
        }
      }
    } catch (error: any) {
      console.error("Error sending invite:", error);
      
      // Check if it's SMTP configuration error
      const errorCode = error?.response?.data?.error?.code;
      if (errorCode === "no_smtp_config" && inviteMethod === "email") {
        // Automatically switch to link method
        message.warning({
          content: "Email service unavailable. Switching to Link method...",
          duration: 3,
        });
        setTimeout(() => {
          setInviteMethod("link");
        }, 1000);
      }
      // Error message is already shown by the mutation hook
    }
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 overflow-y-auto"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="space-y-4 px-4 pb-20 pt-4">

          {/* Header */}
          <div className="flex items-center gap-3 pb-2">
            <button
              onClick={() => navigate(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-card shadow-sm active:scale-95 transition-all"
            >
              <ArrowLeft className="h-5 w-5 text-foreground" strokeWidth={2.5} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-title3 font-bold text-foreground tracking-tight">Add Family Member</h1>
              <p className="text-caption1 text-muted-foreground">Invite to join your group</p>
            </div>
          </div>

          {/* Intro Card — informational only */}
          <section
            aria-label="Why invite family"
            className="cursor-default select-none rounded-xl border border-violet-200/60 bg-gradient-to-br from-violet-50 to-purple-50/60 p-3.5 dark:border-violet-800/30 dark:from-violet-950/40 dark:to-purple-950/20"
          >
            <div className="flex items-start gap-2.5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-purple-600 shadow-sm">
                <Users className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-compact-xs font-semibold uppercase tracking-wide text-muted-foreground">About</p>
                <p className="text-subhead font-bold text-foreground">Track Together</p>
                <p className="text-caption1 text-muted-foreground mt-1 leading-relaxed">
                  Share meals, monitor health & support wellness journey.
                </p>
              </div>
            </div>
          </section>

          {/* Member Name */}
          <div className="space-y-2">
            <label className="text-subhead font-bold text-foreground">Member Name</label>
            <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-200 dark:focus-within:ring-violet-900/40 transition-all">
              <User className="h-5 w-5 text-muted-foreground shrink-0" strokeWidth={2} />
              <input
                type="text"
                placeholder="e.g., John, Sarah"
                value={memberName}
                maxLength={MEMBER_NAME_MAX}
                onChange={(e) =>
                  setMemberName(
                    e.target.value
                      .replace(/[^\p{L}\s\-'.]/gu, "")
                      .slice(0, MEMBER_NAME_MAX),
                  )
                }
                className="flex-1 bg-transparent text-callout text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>
            <p className="px-1 text-right text-compact-xs text-muted-foreground">
              {memberName.length}/{MEMBER_NAME_MAX}
            </p>
          </div>

          {/* Relationship Selector */}
          <div className="space-y-2">
            <label className="text-subhead font-bold text-foreground">Relationship</label>
            <div className="grid grid-cols-2 gap-2">
              {relations.map((r) => {
                const isActive = selectedRelation === r.id;
                const colorMap: Record<string, string> = {
                  blue: isActive
                    ? "border-transparent bg-gradient-primary text-primary-foreground shadow-primary"
                    : "border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-700 text-blue-700 dark:text-blue-300",
                  purple: isActive
                    ? "border-purple-500 bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md shadow-purple-200/50 dark:shadow-purple-900/30"
                    : "border-purple-300 bg-purple-50 dark:bg-purple-950/30 dark:border-purple-700 text-purple-700 dark:text-purple-300",
                  pink: isActive
                    ? "border-pink-500 bg-gradient-to-br from-pink-500 to-pink-600 text-white shadow-md shadow-pink-200/50 dark:shadow-pink-900/30"
                    : "border-pink-300 bg-pink-50 dark:bg-pink-950/30 dark:border-pink-700 text-pink-700 dark:text-pink-300",
                  yellow: isActive
                    ? "border-yellow-500 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white shadow-md shadow-yellow-200/50 dark:shadow-yellow-900/30"
                    : "border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300",
                  cyan: isActive
                    ? "border-cyan-500 bg-gradient-to-br from-cyan-500 to-cyan-600 text-white shadow-md shadow-cyan-200/50 dark:shadow-cyan-900/30"
                    : "border-cyan-300 bg-cyan-50 dark:bg-cyan-950/30 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300",
                  emerald: isActive
                    ? "border-emerald-500 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-200/50 dark:shadow-emerald-900/30"
                    : "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300",
                };
                return (
                  <button
                    key={r.id}
                    onClick={() => setSelectedRelation(r.id)}
                    className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-subhead font-bold transition-all active:scale-95 ${colorMap[r.color]}`}
                  >
                    <r.icon className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                    <span className="flex-1 text-left">{r.label}</span>
                    {isActive && <Check className="h-4 w-4" strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Invite Method Tabs */}
          <div className="space-y-2">
            <label className="text-subhead font-bold text-foreground">Invite via</label>
            <div className="flex gap-1.5 rounded-xl bg-muted p-1">
              <button
                onClick={() => setInviteMethod("email")}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-caption1 font-bold transition-all ${
                  inviteMethod === "email"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                <Mail className="h-4 w-4" />
                Email
              </button>
              <button
                onClick={() => setInviteMethod("link")}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-caption1 font-bold transition-all relative ${
                  inviteMethod === "link"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                <Link className="h-4 w-4" />
                Link
              </button>
            </div>
          </div>

          {/* Contact Input or Link Copy */}
          {inviteMethod === "link" ? (
            <div className="space-y-2">
              <label className="text-subhead font-bold text-foreground">Invite Link</label>
              {generatedLink ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-3">
                    <Link className="h-4 w-4 text-violet-600 dark:text-violet-400 shrink-0" strokeWidth={2.5} aria-hidden />
                    <span className="text-caption1 font-medium text-muted-foreground shrink-0">Link</span>
                    <span className="min-w-0 flex-1 truncate text-caption1 text-foreground">{generatedLink}</span>
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className={`w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-subhead font-bold transition-all active:scale-95 ${
                      inviteLinkCopied
                        ? "bg-emerald-500 text-white"
                        : "bg-violet-500 text-white"
                    }`}
                  >
                    {inviteLinkCopied ? (
                      <>
                        <Check className="h-4 w-4" strokeWidth={2.5} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border/60 bg-muted/30 px-4 py-6 text-center">
                  <Link className="h-8 w-8 mx-auto mb-2 text-muted-foreground" strokeWidth={1.5} />
                  <p className="text-caption1 text-muted-foreground">
                    Click "Generate Link" below to create a shareable invite link
                  </p>
                </div>
              )}
              <p className="text-caption1 text-muted-foreground px-1">
                Anyone with this link can join your family group
              </p>
            </div>
          ) : (
            <div className="space-y-2 min-w-0">
              <label className="text-subhead font-bold text-foreground">
                Email Address
              </label>
              <div className="flex min-w-0 items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-200 dark:focus-within:ring-violet-900/40 transition-all">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={2} />
                <textarea
                  ref={emailFieldRef}
                  inputMode="email"
                  autoComplete="email"
                  rows={1}
                  maxLength={EMAIL_MAX}
                  placeholder="member@example.com"
                  value={contactValue}
                  onChange={(e) =>
                    setContactValue(
                      e.target.value.replace(/\r?\n|\s/g, "").slice(0, EMAIL_MAX),
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  className="max-h-40 min-h-0 min-w-0 flex-1 resize-none overflow-y-auto bg-transparent text-callout leading-normal text-foreground placeholder:text-muted-foreground focus:outline-none break-all"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-1">
                {contactValue && !isEmailValid ? (
                  <p className="text-compact-xs text-destructive">
                    Enter a valid email address
                  </p>
                ) : (
                  <span />
                )}
                <p className="text-right text-compact-xs text-muted-foreground">
                  {contactValue.length}/{EMAIL_MAX}
                </p>
              </div>
            </div>
          )}

          {/* Current Members Preview */}
          {familyMembers && familyMembers.length > 0 && (
            <section
              aria-label="Current group members preview"
              className="cursor-default select-none rounded-xl border border-border/60 bg-muted/25 p-3.5"
            >
              <div className="mb-3 flex items-baseline justify-between gap-2">
                <p className="text-caption1 font-bold uppercase tracking-wide text-muted-foreground">
                  Current group ({familyMembers.length})
                </p>
                <p className="text-compact-xs font-medium text-muted-foreground">Preview · not interactive</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {familyMembers.slice(0, 6).map((member) => {
                  return (
                    <div key={member.user_id} className="flex flex-col items-center">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border/60 bg-violet-500/90 text-subhead font-black text-white ring-1 ring-border/30">
                        {member.initial}
                      </div>
                      <p className="mt-1.5 text-compact-xs font-bold text-foreground truncate max-w-[50px]">
                        {member.is_current_user ? "You" : member.display_name}
                      </p>
                    </div>
                  );
                })}
                {familyMembers.length > 6 && (
                  <div className="flex flex-col items-center">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-dashed border-border bg-muted/40 text-caption1 font-black text-muted-foreground">
                      +{familyMembers.length - 6}
                    </div>
                    <p className="mt-1.5 text-compact-xs font-bold text-muted-foreground">More</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Info Box — tip only, not an action */}
          <aside
            className={`cursor-default select-none rounded-xl border px-3.5 py-3 ${
            inviteMethod === "link" 
              ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200/60 dark:border-blue-800/40"
              : "bg-amber-50 dark:bg-amber-950/30 border-amber-200/60 dark:border-amber-800/40"
          }`}
          >
            <div className="flex items-start gap-2.5">
              <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg mt-0.5 ${inviteMethod === "email" ? "bg-amber-600" : "bg-blue-500"}`}>
                <Info className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <p
                  className={`text-caption1 leading-relaxed ${
                    inviteMethod === "email"
                      ? "text-amber-900 dark:text-amber-200/95"
                      : "text-blue-700 dark:text-blue-300"
                  }`}
                >
                  {inviteMethod === "email"
                    ? "An invite email will be sent to your family member to join your health group."
                    : "Share this link with your family member to invite them to your health group."}
                </p>
              </div>
            </div>
          </aside>
          {/* Send Invite Button */}
          <div className="pt-2">
            <button
              onClick={handleSendInvite}
              disabled={
                sendInviteMutation.isPending ||
                (inviteMethod === "email" && !isEmailValid)
              }
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-3.5 text-subhead font-bold text-white shadow-md active:scale-98 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendInviteMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} />
                  Sending...
                </>
              ) : inviteMethod === "link" && !generatedLink ? (
                <>
                  <Sparkles className="h-5 w-5" strokeWidth={2.5} />
                  Generate Link
                </>
              ) : inviteMethod === "link" ? (
                <>
                  <Check className="h-5 w-5" strokeWidth={2.5} />
                  Done
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" strokeWidth={2.5} />
                  Send Invite
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default InviteMemberPage;
